import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Alert,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Description,
  PictureAsPdf,
  Image,
  AttachFile,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
} from '@mui/icons-material';
import { DOCUMENT_TYPES, DOCUMENT_TYPE_LABELS } from '../../services/leads';

const BulkDocumentUpload = ({
  open,
  onClose,
  onUpload,
  leadId,
  loading = false,
  existingDocuments = []
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileTypes, setFileTypes] = useState([]);
  const [fileDescriptions, setFileDescriptions] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef();

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setError('');
    setUploadResults(null);

    const validFiles = [];
    const newFileTypes = [];
    const newFileDescriptions = [];

    files.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        setError(prev => prev + `File ${file.name} is too large. Maximum size is 10MB.\n`);
        return;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(prev => prev + `File ${file.name} has invalid type. Allowed: PDF, JPG, JPEG, PNG, GIF.\n`);
        return;
      }
      validFiles.push(file);
      newFileTypes.push('');
      newFileDescriptions.push('');
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
    setFileTypes(prev => [...prev, ...newFileTypes]);
    setFileDescriptions(prev => [...prev, ...newFileDescriptions]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFileTypes(prev => prev.filter((_, i) => i !== index));
    setFileDescriptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileTypeChange = (index, value) => {
    setFileTypes(prev => prev.map((type, i) => i === index ? value : type));
  };

  const handleDescriptionChange = (index, value) => {
    setFileDescriptions(prev => prev.map((desc, i) => i === index ? value : desc));
  };

  const handleBulkUpload = async () => {
    // Validate all files have document types
    const missingTypes = fileTypes.some(type => !type);
    if (missingTypes) {
      setError('Please select document type for all files');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      // Prepare descriptions with fallbacks
      const descriptions = fileDescriptions.map((desc, index) =>
        desc || `${DOCUMENT_TYPE_LABELS[fileTypes[index]]} - ${selectedFiles[index].name}`
      );

      // Use the new service method signature
      const response = await onUpload(leadId, selectedFiles, fileTypes, descriptions, (progress) => {
        setUploadProgress(progress);
      });

      setUploadResults(response);

      if (response.error_count === 0) {
        // All files uploaded successfully
        setTimeout(() => {
          setSelectedFiles([]);
          setFileTypes([]);
          setFileDescriptions([]);
          setUploadResults(null);
          onClose();
        }, 2000);
      }
    } catch (error) {
      setError('Failed to upload documents. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType === 'application/pdf') return <PictureAsPdf color="error" />;
    if (fileType.startsWith('image/')) return <Image color="primary" />;
    return <AttachFile color="action" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const canUpload = selectedFiles.length > 0 && fileTypes.every(type => type) && !isUploading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={isUploading}
    >
      <DialogTitle>
        Bulk Upload Documents
        {leadId && (
          <Typography variant="caption" display="block" color="text.secondary">
            Lead ID: {leadId}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{error}</pre>
          </Alert>
        )}

        {uploadResults && (
          <Alert
            severity={uploadResults.error_count === 0 ? 'success' : 'warning'}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              {uploadResults.message}
            </Typography>
            <Typography variant="caption" display="block">
              Total: {uploadResults.total_files} •
              Success: {uploadResults.success_count} •
              Errors: {uploadResults.error_count}
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* File Upload Section */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Upload New Documents
              </Typography>
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif"
                onChange={handleFileSelect}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ ml: 2 }}
                disabled={isUploading}
              >
                Select Files
              </Button>
            </Box>

            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
              Allowed: PDF, JPG, JPEG, PNG, GIF • Max size: 10MB per file
            </Typography>

            {isUploading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Uploading {selectedFiles.length} files...
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            {/* Selected Files */}
            {selectedFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files ({selectedFiles.length})
                </Typography>
                {selectedFiles.map((file, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          {getFileIcon(file.type)}
                          <Box>
                            <Typography variant="body2" noWrap>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(file.size)}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveFile(index)}
                          disabled={isUploading}
                        >
                          <Delete />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Document Type *</InputLabel>
                            <Select
                              value={fileTypes[index] || ''}
                              onChange={(e) => handleFileTypeChange(index, e.target.value)}
                              label="Document Type *"
                              disabled={isUploading}
                            >
                              {Object.entries(DOCUMENT_TYPE_LABELS).map(([key, label]) => (
                                <MenuItem key={key} value={key}>
                                  {label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Description (Optional)"
                            value={fileDescriptions[index] || ''}
                            onChange={(e) => handleDescriptionChange(index, e.target.value)}
                            disabled={isUploading}
                            placeholder="Add description..."
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Upload Results */}
            {uploadResults && uploadResults.errors && uploadResults.errors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="error">
                  Upload Errors
                </Typography>
                <List dense>
                  {uploadResults.errors.map((error, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <ErrorIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={error.filename}
                        secondary={Object.values(error.errors).flat().join(', ')}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {uploadResults && uploadResults.uploaded_documents && uploadResults.uploaded_documents.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom color="success.main">
                  Successfully Uploaded
                </Typography>
                <List dense>
                  {uploadResults.uploaded_documents.map((doc, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.filename}
                        secondary={`${DOCUMENT_TYPE_LABELS[doc.document_type]} • ${formatFileSize(doc.size)}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Grid>

          {/* Existing Documents Section */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Existing Documents
            </Typography>

            {!Array.isArray(existingDocuments) || existingDocuments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No documents uploaded yet.
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {Object.entries(DOCUMENT_TYPE_LABELS).map(([type, label]) => {
                  const docs = existingDocuments.filter(doc => doc.document_type === type);
                  if (docs.length === 0) return null;

                  return (
                    <Box key={type} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {label}
                        <Chip
                          label={docs.length}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                      {docs.map((doc) => (
                        <Card key={doc.id} variant="outlined" sx={{ mb: 1 }}>
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Description fontSize="small" />
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" noWrap>
                                  {doc.file_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {doc.description}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleBulkUpload}
          disabled={!canUpload}
          startIcon={<CloudUpload />}
        >
          {isUploading ? `Uploading... ${uploadProgress}%` : `Upload ${selectedFiles.length} File(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkDocumentUpload;